import React, { useEffect, useMemo, useRef, useState } from "react";
import socket from "../lib/socket";

function channelSlugFromRoom(room) {
  const value = String(room || "").trim().toLowerCase();
  if (!value) return "";
  const parts = value.split(":");
  return String(parts[parts.length - 1] || "")
    .trim()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 80);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0));
}

function playGasPointsAlert(contextRef, eventType, requestedVolume = 1) {
  if (typeof window === "undefined") return;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;

  let context = contextRef.current;
  if (!context || context.state === "closed") {
    context = new AudioContextClass();
    contextRef.current = context;
  }

  const play = () => {
    const volume = clamp(requestedVolume, 0, 1.5);
    if (volume <= 0) return;

    const startAt = context.currentTime + 0.025;
    const finishAt = startAt + 1.45;
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();

    compressor.threshold.setValueAtTime(-18, startAt);
    compressor.knee.setValueAtTime(20, startAt);
    compressor.ratio.setValueAtTime(8, startAt);
    compressor.attack.setValueAtTime(0.004, startAt);
    compressor.release.setValueAtTime(0.22, startAt);

    master.gain.setValueAtTime(0.0001, startAt);
    master.gain.exponentialRampToValueAtTime(0.42 * volume, startAt + 0.035);
    master.gain.exponentialRampToValueAtTime(0.0001, finishAt);
    master.connect(compressor);
    compressor.connect(context.destination);

    const notes =
      eventType === "emote"
        ? [392, 523.25, 659.25, 783.99]
        : [261.63, 392, 523.25, 659.25];

    notes.forEach((frequency, index) => {
      const when = startAt + index * 0.17;
      const duration = index === notes.length - 1 ? 0.56 : 0.34;
      const oscillator = context.createOscillator();
      const noteGain = context.createGain();

      oscillator.type = index % 2 === 0 ? "triangle" : "sine";
      oscillator.frequency.setValueAtTime(frequency, when);
      oscillator.frequency.exponentialRampToValueAtTime(
        frequency * 1.018,
        when + duration
      );

      noteGain.gain.setValueAtTime(0.0001, when);
      noteGain.gain.exponentialRampToValueAtTime(0.78, when + 0.018);
      noteGain.gain.exponentialRampToValueAtTime(
        0.0001,
        when + duration
      );

      oscillator.connect(noteGain);
      noteGain.connect(master);
      oscillator.start(when);
      oscillator.stop(when + duration + 0.04);
    });

    const bass = context.createOscillator();
    const bassGain = context.createGain();
    bass.type = "sine";
    bass.frequency.setValueAtTime(132, startAt);
    bass.frequency.exponentialRampToValueAtTime(66, startAt + 0.48);
    bassGain.gain.setValueAtTime(0.0001, startAt);
    bassGain.gain.exponentialRampToValueAtTime(0.3, startAt + 0.025);
    bassGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.52);
    bass.connect(bassGain);
    bassGain.connect(master);
    bass.start(startAt);
    bass.stop(startAt + 0.56);
  };

  if (context.state === "suspended") {
    context.resume().then(play).catch(() => {});
  } else {
    play();
  }
}

export default function GasPointsOverlayLayer({
  room = "",
  soundEnabled = true,
  alertVolume = 1,
  debug = false,
}) {
  const [active, setActive] = useState(null);
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const activeRef = useRef(null);
  const seenRef = useRef(new Set());
  const audioContextRef = useRef(null);
  const channelSlug = useMemo(() => channelSlugFromRoom(room), [room]);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(
    () => () => {
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context && context.state !== "closed") {
        context.close().catch(() => {});
      }
    },
    []
  );

  useEffect(() => {
    function showNext() {
      if (activeRef.current || queueRef.current.length === 0) return;
      const next = queueRef.current.shift();
      if (soundEnabled) {
        playGasPointsAlert(audioContextRef, next?.type, alertVolume);
      }
      activeRef.current = next;
      setActive(next);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        activeRef.current = null;
        setActive(null);
        window.setTimeout(showNext, 180);
      }, Math.max(3200, Number(next?.durationMs || 6500)));
    }

    function joinOverlayChannel() {
      if (!channelSlug) return;
      socket.emit("gas-points:join", { channelSlug });
    }

    function onOverlay(payload = {}) {
      const eventId = String(payload?.id || "").trim();
      const eventChannel = channelSlugFromRoom(payload?.channelSlug);
      if (!eventId || !channelSlug || eventChannel !== channelSlug) return;
      if (seenRef.current.has(eventId)) return;

      seenRef.current.add(eventId);
      if (seenRef.current.size > 100) {
        seenRef.current = new Set(Array.from(seenRef.current).slice(-60));
      }

      queueRef.current.push(payload);
      showNext();
    }

    socket.on("connect", joinOverlayChannel);
    socket.on("gas-points:overlay", onOverlay);

    if (socket.connected) {
      joinOverlayChannel();
    } else {
      socket.connect();
    }

    const debugTimer =
      debug && channelSlug
        ? window.setTimeout(() => {
            onOverlay({
              id: `gas-points-debug-${Date.now()}`,
              type: "message",
              channelSlug,
              username: "GA TEST",
              message: "GAS Points OBS alert is connected!",
              durationMs: 8000,
              createdAt: Date.now(),
            });
          }, 650)
        : null;

    return () => {
      if (socket.connected && channelSlug) {
        socket.emit("gas-points:leave", { channelSlug });
      }
      socket.off("connect", joinOverlayChannel);
      socket.off("gas-points:overlay", onOverlay);
      window.clearTimeout(timerRef.current);
      if (debugTimer) window.clearTimeout(debugTimer);
      queueRef.current = [];
      activeRef.current = null;
      seenRef.current = new Set();
    };
  }, [channelSlug, soundEnabled, alertVolume, debug]);

  if (!active) return null;

  const isEmote = active.type === "emote" && active?.emote?.src;

  return (
    <div
      aria-live="polite"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 24,
        pointerEvents: "none",
        display: "grid",
        placeItems: "center",
        padding: "7%",
      }}
    >
      <style>
        {"@keyframes gaPointsOverlayIn { 0% { opacity: 0; transform: translateY(24px) scale(.82) rotate(-1deg); } 12% { opacity: 1; transform: translateY(0) scale(1.04) rotate(0); } 20%, 78% { opacity: 1; transform: translateY(0) scale(1); } 100% { opacity: 0; transform: translateY(-18px) scale(.94); } } @keyframes gaPointsEmotePulse { 0%, 100% { filter: drop-shadow(0 10px 24px rgba(0,0,0,.75)); } 50% { filter: drop-shadow(0 0 30px rgba(255,200,50,.8)); } }"}
      </style>
      <div
        key={active.id}
        style={{
          width: isEmote ? "min(360px, 48%)" : "min(720px, 82%)",
          textAlign: "center",
          animation:
            "gaPointsOverlayIn " +
            Math.max(3.2, Number(active.durationMs || 6500) / 1000) +
            "s ease both",
        }}
      >
        {isEmote ? (
          <>
            <img
              src={active.emote.src}
              alt={active.emote.label || "GA emote"}
              style={{
                width: "100%",
                maxHeight: "34vh",
                objectFit: "contain",
                display: "block",
                animation: "gaPointsEmotePulse 1.4s ease-in-out infinite",
              }}
            />
            <div
              style={{
                display: "inline-block",
                marginTop: 8,
                padding: "8px 15px",
                borderRadius: 999,
                background: "rgba(0,0,0,.78)",
                border: "1px solid rgba(255,215,80,.42)",
                color: "white",
                fontSize: "clamp(13px, 1.5vw, 21px)",
                fontWeight: 1000,
                textShadow: "0 2px 6px #000",
              }}
            >
              @{active.username || "GA member"} dropped{" "}
              {active.emote.label || "a GA emote"} ⛽
            </div>
          </>
        ) : (
          <div
            style={{
              padding: "clamp(18px, 3vw, 34px)",
              borderRadius: 24,
              background:
                "radial-gradient(circle at 20% 0%, rgba(255,195,50,.26), transparent 48%), linear-gradient(135deg, rgba(25,8,12,.96), rgba(90,0,18,.94))",
              border: "2px solid rgba(255,215,80,.58)",
              boxShadow:
                "0 24px 70px rgba(0,0,0,.72), inset 0 1px 0 rgba(255,255,255,.16)",
              color: "white",
            }}
          >
            <div
              style={{
                color: "#ffd65c",
                fontSize: "clamp(12px, 1.4vw, 20px)",
                fontWeight: 1000,
                textTransform: "uppercase",
                letterSpacing: 1.3,
              }}
            >
              GAS Points spotlight • @{active.username || "GA member"}
            </div>
            <div
              style={{
                marginTop: 10,
                fontSize: "clamp(22px, 3.6vw, 52px)",
                lineHeight: 1.08,
                fontWeight: 1000,
                overflowWrap: "anywhere",
                textShadow: "0 4px 14px rgba(0,0,0,.9)",
              }}
            >
              {active.message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

